import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { TraceLogs } from '../../../shared/models/trace-logs';
import { transformTraceLogsResponse } from '../../../shared/utils/transform-trace-logs-response';
import { SignRequest } from '../models/sign-request';
import { SignRequestAttachParams } from '../models/sign-request-attach-params';
import { SignRequestBulkCustomerMutationParams } from '../models/sign-request-bulk-customer-mutation-params';
import { SignRequestDetails } from '../models/sign-request-details';
import { SignRequestMutationParams } from '../models/sign-request-mutation-params';
import { SignRequestParams } from '../models/sign-request-params';
import { RcFile } from 'antd/es/upload';
import { getBase64, getFileDigest, spliceBase64 } from '../../../shared/utils/file-handling/file-handling';
import { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';

export const signRequestsApi = api
  .enhanceEndpoints({
    addTagTypes: ['SignRequests', 'Request', 'FileUpload'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getSignRequests: builder.query<PaginatedList<SignRequest>, SignRequestParams>({
        query: body => ({
          url: '/portal-api/v2/sign-requests/list',
          method: 'POST',
          body,
        }),
        providesTags: ['SignRequests', 'FileUpload'],
      }),
      getSignRequest: builder.query<SignRequestDetails, string>({
        query: id => ({
          url: `/portal-api/v1/sign-requests/${id}`,
        }),
      }),
      createSignRequest: builder.mutation<SignRequestDetails, { file?: RcFile } & SignRequestMutationParams>({
        async queryFn(data, _queryApi, _extraOptions, baseQuery) {
          const { file, ...body } = data;
          if (file) {
            return getBase64(file).then(async fileData => {
              const base64Result = spliceBase64(fileData);
              const digest = await getFileDigest(file);
              return baseQuery({
                url: `/portal-api/v1/sign-requests`,
                method: 'POST',
                body: {
                  digest,
                  content: base64Result,
                  ...body,
                },
              }) as QueryReturnValue<SignRequestDetails, FetchBaseQueryError, FetchBaseQueryMeta>;
            });
          }
          return baseQuery({
            url: `/portal-api/v1/sign-requests`,
            method: 'POST',
            body: data,
          }) as QueryReturnValue<SignRequestDetails, FetchBaseQueryError, FetchBaseQueryMeta>;
        },
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      createBulkCustomerSignRequest: builder.mutation<SignRequestDetails, SignRequestBulkCustomerMutationParams>({
        query: body => ({
          url: '/portal-api/v1/bulk-customer-sign-requests',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      attachSignRequest: builder.mutation<SignRequestDetails, SignRequestAttachParams>({
        query: ({ signRequestId, ...body }) => ({
          url: `/portal-api/v1/sign-requests/${signRequestId}/attach`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      getSignRequestsTraceLogs: builder.query<PaginatedList<TraceLogs>, { id: string; page: number }>({
        query: ({ id, ...params }) => ({
          url: `/portal-api/v1/sign-requests/${id}/trace-logs`,
          params,
        }),
        providesTags: ['SignRequests'],
        transformResponse: transformTraceLogsResponse,
      }),
      updateSignRequest: builder.mutation<SignRequestDetails, Pick<SignRequest, 'name' | 'id'>>({
        query: body => ({
          url: `/portal-api/v1/sign-requests/${body.id}`,
          method: 'PUT',
          body: body,
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      detachSignRequest: builder.mutation<SignRequestDetails, string>({
        query: id => ({
          url: `/portal-api/v1/sign-requests/${id}/detach`,
          method: 'POST',
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      deleteSignRequest: builder.mutation<void, string>({
        query: id => ({
          url: `/portal-api/v1/sign-requests/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      restoreSignRequest: builder.mutation<void, string>({
        query: id => ({
          url: `/portal-api/v1/sign-requests/${id}/restore`,
          method: 'POST',
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
      emailSignRequest: builder.mutation<void, { email: string; phone: string; id: string }>({
        query: ({ id, ...body }) => ({
          url: `/portal-api/v1/sign-requests/${id}/email`,
          method: 'POST',
          body,
        }),
      }),
      archiveSignRequest: builder.mutation<void, { signRequests: string[] }>({
        query: body => ({
          url: '/portal-api/v1/sign-requests/archive',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['SignRequests', 'Request'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useCreateSignRequestMutation,
  useCreateBulkCustomerSignRequestMutation,
  useGetSignRequestsQuery,
  useGetSignRequestsTraceLogsQuery,
  useAttachSignRequestMutation,
  useUpdateSignRequestMutation,
  useDetachSignRequestMutation,
  useDeleteSignRequestMutation,
  useRestoreSignRequestMutation,
  useEmailSignRequestMutation,
  useArchiveSignRequestMutation,
} = signRequestsApi;
